/**
 * Referral Period Utilities
 *
 * Pure module — no 'use server', no Node-only imports.
 * Safe to import in both client and server components.
 *
 * TIMEZONE CHOICE: All boundaries are derived in UTC.
 * Invoices are stored as `invoice_date timestamptz` and compared with
 * `>= start` (inclusive) and `< end` (exclusive, i.e. first instant of the
 * next period) using UTC month/year arithmetic.
 *
 * This means "last day of a month" (e.g. Jan 31 23:59:59 UTC) correctly
 * falls inside that month's bucket because it is < Feb 01 00:00:00 UTC.
 * All arithmetic uses Date.UTC to avoid local-timezone drift on the server.
 */

export type ReferralPeriod = 'this_month' | 'last_month' | 'last_3_months' | 'this_year' | 'all';

export const REFERRAL_PERIODS: { key: ReferralPeriod; label: string }[] = [
  { key: 'this_month', label: 'This Month' },
  { key: 'last_month', label: 'Last Month' },
  { key: 'last_3_months', label: 'Last 3 Months' },
  { key: 'this_year', label: 'This Year' },
  { key: 'all', label: 'All Time' },
];

export interface ResolvedPeriod {
  start: Date | null;
  end: Date | null;
  label: string;
}

/**
 * Resolve a ReferralPeriod key into UTC-anchored Date boundaries.
 *
 * Boundary semantics:
 *   - start: inclusive (>= start)
 *   - end:   exclusive (< end) — set to the first instant of the period AFTER the window
 *
 * Example: 'last_month' for February 2026
 *   start = 2026-02-01T00:00:00.000Z
 *   end   = 2026-03-01T00:00:00.000Z  (first instant of March)
 *
 * An invoice on 2026-02-28 23:59:59Z satisfies: start <= invoice_date < end ✓
 *
 * @param key - The period preset
 * @param now - Optional "current time" for deterministic testing. Defaults to new Date().
 */
export function resolveReferralPeriod(key: ReferralPeriod, now: Date = new Date()): ResolvedPeriod {
  const label = REFERRAL_PERIODS.find((p) => p.key === key)?.label ?? key;

  if (key === 'all') {
    return { start: null, end: null, label };
  }

  // Derive current year/month in UTC
  const year = now.getUTCFullYear();
  const month = now.getUTCMonth(); // 0-based

  switch (key) {
    case 'this_month': {
      const start = new Date(Date.UTC(year, month, 1));
      const end = new Date(Date.UTC(year, month + 1, 1));
      return { start, end, label };
    }
    case 'last_month': {
      // month-1 may be -1 (January) → Date.UTC handles wrap correctly
      const start = new Date(Date.UTC(year, month - 1, 1));
      const end = new Date(Date.UTC(year, month, 1));
      return { start, end, label };
    }
    case 'last_3_months': {
      const start = new Date(Date.UTC(year, month - 2, 1));
      const end = new Date(Date.UTC(year, month + 1, 1));
      return { start, end, label };
    }
    case 'this_year': {
      const start = new Date(Date.UTC(year, 0, 1));
      const end = new Date(Date.UTC(year + 1, 0, 1));
      return { start, end, label };
    }
  }
}

export function isValidReferralPeriod(x: string): x is ReferralPeriod {
  return ['this_month', 'last_month', 'last_3_months', 'this_year', 'all'].includes(x);
}
